
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from '@/components/ui/label';
import { Clock, Waves, AirVent, Users, Utensils, Hotel } from 'lucide-react';

interface HotelDetailsProps {
  place: any;
  isEditing: boolean;
  onInputChange: (field: string, value: any) => void;
}

export function HotelDetails({ place, isEditing, onInputChange }: HotelDetailsProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Hotel className="w-5 h-5" />
        Hotel Details
      </h3>

      {isEditing ? (
        <>
          <div className="space-y-2">
            <Label>Hotel Class (stars)</Label>
            <Select
              value={String(place.hotel_class || '')}
              onValueChange={(value) => onInputChange('hotel_class', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select hotel class" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((stars) => (
                  <SelectItem key={stars} value={String(stars)}>
                    {'⭐'.repeat(stars)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Check-in Time</Label>
              <Input
                value={place.check_in_time || ''}
                onChange={(e) => onInputChange('check_in_time', e.target.value)}
                placeholder="e.g., 3:00 PM"
              />
            </div>

            <div className="space-y-2">
              <Label>Check-out Time</Label>
              <Input
                value={place.check_out_time || ''}
                onChange={(e) => onInputChange('check_out_time', e.target.value)}
                placeholder="e.g., 11:00 AM"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={place.has_pool}
                onCheckedChange={(checked) => onInputChange('has_pool', checked)}
              />
              <Label>Swimming Pool</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={place.has_spa}
                onCheckedChange={(checked) => onInputChange('has_spa', checked)}
              />
              <Label>Spa</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={place.has_fitness_center}
                onCheckedChange={(checked) => onInputChange('has_fitness_center', checked)}
              />
              <Label>Fitness Center</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={place.has_room_service}
                onCheckedChange={(checked) => onInputChange('has_room_service', checked)}
              />
              <Label>Room Service</Label>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {place.hotel_class && (
            <div className="flex items-center gap-2">
              <span>{'⭐'.repeat(place.hotel_class)}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {place.check_in_time && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span>Check-in: {place.check_in_time}</span>
              </div>
            )}

            {place.check_out_time && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span>Check-out: {place.check_out_time}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-4">
            {place.has_pool && (
              <Badge variant="outline">
                <Waves className="w-4 h-4 mr-1" />
                Pool
              </Badge>
            )}
            {place.has_spa && (
              <Badge variant="outline">
                <AirVent className="w-4 h-4 mr-1" />
                Spa
              </Badge>
            )}
            {place.has_fitness_center && (
              <Badge variant="outline">
                <Users className="w-4 h-4 mr-1" />
                Fitness Center
              </Badge>
            )}
            {place.has_room_service && (
              <Badge variant="outline">
                <Utensils className="w-4 h-4 mr-1" />
                Room Service
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
